/**
 * Compact mobile header for Job Seeker Dashboard
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faEnvelope,
  faBriefcase,
  faSignOutAlt,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import notificationApi from '../api/notificationApi';
import './JobSeekerDashboardMobileHeader.css';

const JobSeekerDashboardMobileHeader = ({ onOpenMessages }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [profilePhotoPath, setProfilePhotoPath] = useState(null);
  const [avatarError, setAvatarError] = useState(false);
  const userMenuRef = useRef(null);
  const notificationBtnRef = useRef(null);
  const messageBtnRef = useRef(null);
  const profileDropdownRef = useRef(null);
  const notificationDropdownRef = useRef(null);

  const closeAllMenus = useCallback(() => {
    setShowUserMenu(false);
    setShowNotifications(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inAvatar = userMenuRef.current?.contains(event.target);
      const inProfileMenu = profileDropdownRef.current?.contains(event.target);
      const inNotificationBtn = notificationBtnRef.current?.contains(event.target);
      const inNotificationMenu = notificationDropdownRef.current?.contains(event.target);
      const inMessageBtn = messageBtnRef.current?.contains(event.target);

      if (!inAvatar && !inProfileMenu) {
        setShowUserMenu(false);
      }
      if (!inNotificationBtn && !inNotificationMenu) {
        setShowNotifications(false);
      }
      if (!inMessageBtn && !inAvatar && !inProfileMenu && !inNotificationBtn && !inNotificationMenu) {
        /* message button opens section directly */
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCounts = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const [notifRes, msgRes] = await Promise.all([
        axios.get(buildApiUrl('/api/notifications'), { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 }),
        axios.get(buildApiUrl('/api/messages'), { headers: { Authorization: `Bearer ${token}` }, timeout: 5000 })
      ]);

      if (notifRes.data?.success) {
        const list = notifRes.data.notifications || [];
        setNotifications(list);
        setNotificationCount(list.filter((n) => !n.read).length);
      }
      if (msgRes.data?.success) {
        const list = msgRes.data.messages || [];
        setMessageCount(list.filter((m) => !m.read).length);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    fetchCounts();
    const interval = setInterval(fetchCounts, 15000);
    return () => clearInterval(interval);
  }, [fetchCounts]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios
      .get(buildApiUrl('/api/jobseeker/profile'), { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => {
        const photo = response.data?.profilePhotoPath || response.data?.profilePhoto || response.data?.profileImage;
        if (photo) {
          setProfilePhotoPath(photo);
          setAvatarError(false);
        }
      })
      .catch(() => {});
  }, []);

  const getProfilePhotoUrl = (path) => {
    if (!path) return null;
    if (typeof path === 'string' && (path.startsWith('http://') || path.startsWith('https://'))) return path;
    const normalized = path.replace(/\\/g, '/');
    if (normalized.includes('jobseeker_profiles/photos')) {
      const uploadPath = normalized
        .replace('uploads/jobseeker_profiles/photos/', '')
        .replace('jobseeker_profiles/photos/', '');
      return buildApiUrl(`/api/users/uploads/jobseeker_profiles/photos/${uploadPath}`);
    }
    if (normalized.startsWith('uploads/')) {
      return buildApiUrl(`/api/users/uploads/${normalized.replace('uploads/', '')}`);
    }
    return buildApiUrl(`/api/users/uploads/${normalized}`);
  };

  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    return user?.email ? user.email.substring(0, 2).toUpperCase() : 'JS';
  };

  const getUserName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user?.email || 'Job Seeker';
  };

  const handleLogout = () => {
    closeAllMenus();
    logout();
    navigate('/login');
  };

  const handleNotificationToggle = () => {
    setShowUserMenu(false);
    setShowNotifications((open) => !open);
  };

  const handleMessagesOpen = () => {
    closeAllMenus();
    if (typeof onOpenMessages === 'function') {
      onOpenMessages();
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    await notificationApi.markAllAsRead();
    fetchCounts();
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read && notification._id) {
      await notificationApi.markAsRead(notification._id);
      fetchCounts();
    }
  };

  const photoUrl = user?.profileImage || (profilePhotoPath ? getProfilePhotoUrl(profilePhotoPath) : null);
  const showAvatarImage = Boolean(photoUrl) && !avatarError;

  return (
    <header className="js-mobile-header">
      <div
        className="js-mobile-header-brand"
        onClick={() => navigate('/jobseeker-dashboard')}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/jobseeker-dashboard'); }}
        role="button"
        tabIndex={0}
        aria-label="Go to dashboard home"
      >
        <div className="js-mobile-logo-icon" aria-hidden="true">
          <FontAwesomeIcon icon={faBriefcase} />
        </div>
        <div className="js-mobile-logo-text">
          <span className="js-mobile-logo-name">Jobseeker Hub</span>
          <span className="js-mobile-logo-subtitle">AksharJobs</span>
        </div>
      </div>

      <div className="js-mobile-header-actions">
        <button
          ref={notificationBtnRef}
          type="button"
          className="js-mobile-action-btn btn-custom-size"
          aria-label="Notifications"
          aria-expanded={showNotifications}
          onClick={handleNotificationToggle}
        >
          <FontAwesomeIcon icon={faBell} className="js-mobile-action-icon" />
          {notificationCount > 0 && <span className="js-mobile-badge">{notificationCount}</span>}
        </button>

        <button
          ref={messageBtnRef}
          type="button"
          className="js-mobile-action-btn btn-custom-size"
          aria-label="Messages"
          onClick={handleMessagesOpen}
        >
          <FontAwesomeIcon icon={faEnvelope} className="js-mobile-action-icon" />
          {messageCount > 0 && <span className="js-mobile-badge">{messageCount}</span>}
        </button>

        <button
          type="button"
          className="js-mobile-action-btn js-mobile-action-btn--avatar btn-custom-size"
          aria-label="Account menu"
          ref={userMenuRef}
          onClick={() => {
            setShowNotifications(false);
            setShowUserMenu((open) => !open);
          }}
        >
          {showAvatarImage ? (
            <img
              src={photoUrl}
              alt={getUserName()}
              className="js-mobile-action-avatar-img"
              onError={() => setAvatarError(true)}
            />
          ) : (
            <span className="js-mobile-avatar-initials">{getUserInitials()}</span>
          )}
        </button>

        {showNotifications && ReactDOM.createPortal(
          <div ref={notificationDropdownRef} className="js-mobile-dropdown-menu js-mobile-notifications-menu">
            <div className="js-mobile-dropdown-header">
              <h4>Notifications</h4>
              {notificationCount > 0 && (
                <button type="button" className="js-mobile-dropdown-action" onClick={handleMarkAllNotificationsRead}>
                  Mark all read
                </button>
              )}
            </div>
            <div className="js-mobile-dropdown-body">
              {notifications.length > 0 ? (
                notifications.slice(0, 12).map((notification, index) => (
                  <button
                    key={notification._id || index}
                    type="button"
                    className={`js-mobile-dropdown-item${notification.read ? '' : ' unread'}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <span className="js-mobile-dropdown-item-title">
                      {notification.message || notification.title || 'Notification'}
                    </span>
                    {notification.body && (
                      <span className="js-mobile-dropdown-item-subtitle">{notification.body}</span>
                    )}
                  </button>
                ))
              ) : (
                <div className="js-mobile-dropdown-empty">No new notifications</div>
              )}
            </div>
          </div>,
          document.body
        )}

        {showUserMenu && ReactDOM.createPortal(
          <div ref={profileDropdownRef} className="js-mobile-profile-menu">
            <div className="js-mobile-profile-menu-header">
              <div className="js-mobile-profile-menu-avatar">
                {showAvatarImage ? (
                  <img src={photoUrl} alt={getUserName()} onError={() => setAvatarError(true)} />
                ) : (
                  <span>{getUserInitials()}</span>
                )}
              </div>
              <div className="js-mobile-profile-menu-info">
                <div className="js-mobile-profile-menu-name">{getUserName()}</div>
                <div className="js-mobile-profile-menu-email">{user?.email}</div>
              </div>
            </div>

            <div className="js-mobile-profile-menu-body">
              <div
                className="js-mobile-profile-menu-item"
                role="button"
                tabIndex={0}
                onClick={() => { setShowUserMenu(false); navigate('/profile'); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setShowUserMenu(false); navigate('/profile'); } }}
              >
                <FontAwesomeIcon icon={faUser} />
                <span>My Profile</span>
              </div>

              <div className="js-mobile-profile-menu-divider" />

              <div
                className="js-mobile-profile-menu-item js-mobile-profile-menu-logout"
                role="button"
                tabIndex={0}
                onClick={handleLogout}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleLogout(); }}
              >
                <FontAwesomeIcon icon={faSignOutAlt} />
                <span>Logout</span>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </header>
  );
};

export default JobSeekerDashboardMobileHeader;
